"use client";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { detectiOS } from "./utils";

/**
 * React hook for triggering haptic feedback on mobile devices
 *
 * This hook creates hidden DOM elements to trigger haptic feedback using the `input[switch]`
 * element for iOS devices and falls back to the Vibration API for other supported devices.
 *
 * @param {number | number[]} pattern - The vibration pattern in milliseconds. Can be a single duration or an array (e.g., [vibrate, pause, vibrate]).
 * @returns {Object} An object containing the `triggerHaptic` function.
 *
 * @example
 * ```tsx
 * import { useHaptic } from "./use-haptic"; // Assuming it's in the same directory
 *
 * function HapticButton() {
 *   const { triggerHaptic } = useHaptic(150); // Single 150ms vibration
 *   const { triggerHaptic: triggerDouble } = useHaptic([50, 50, 50]); // Double 50ms vibration with 50ms pause
 *
 *   return (
 *     <>
 *       <button onClick={triggerHaptic}>Vibrate Once</button>
 *       <button onClick={triggerDouble}>Vibrate Double</button>
 *     </>
 *   );
 * }
 * ```
 */
export const useHaptic = (
  pattern: number | number[] // Changed duration to pattern
): { triggerHaptic: () => void } => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const labelRef = useRef<HTMLLabelElement | null>(null);
  const isIOS = useMemo(() => detectiOS(), []);

  useEffect(() => {
    // Create and append input element (consider making this global/singleton later if performance issues arise)
    const input = document.createElement("input");
    input.type = "checkbox";
    // Use a more unique ID, perhaps based on a random number or timestamp if needed
    // For now, keep it simple, but be aware of potential conflicts if multiple instances exist.
    input.id = `haptic-switch-${Math.random().toString(36).substring(7)}`;
    input.setAttribute("switch", "");
    input.style.display = "none";
    document.body.appendChild(input);
    inputRef.current = input;

    // Create and append label element
    const label = document.createElement("label");
    label.htmlFor = input.id;
    label.style.display = "none";
    document.body.appendChild(label);
    labelRef.current = label;

    // Cleanup function
    return () => {
      // Check if elements still exist before removing
      if (inputRef.current && document.body.contains(inputRef.current)) {
        document.body.removeChild(inputRef.current);
      }
      if (labelRef.current && document.body.contains(labelRef.current)) {
        document.body.removeChild(labelRef.current);
      }
    };
  }, []); // Empty dependency array ensures this runs only once per hook instance

  const triggerHaptic = useCallback(() => {
    if (isIOS) {
      // On iOS, trigger the standard haptic.
      // For patterns suggesting multiple vibrations, trigger it multiple times.
      const clickCount = Array.isArray(pattern)
        ? Math.ceil(pattern.filter((_, i) => i % 2 === 0).length) // Count vibrate intervals
        : 1;

      for (let i = 0; i < clickCount; i++) {
        // Add a small delay between clicks for the 'double' effect attempt
        setTimeout(() => labelRef.current?.click(), i * 250); // 60ms delay might work
      }
    } else {
      // Use Vibration API for non-iOS devices
      if (navigator?.vibrate) {
        try {
          navigator.vibrate(pattern);
        } catch (error) {
          console.error("Vibration API error:", error);
          // Fallback maybe?
        }
      } else {
        // Optional: Fallback to iOS method if Vibration API is unavailable but it's not iOS (unlikely edge case)
        // labelRef.current?.click();
        console.warn("Vibration API not supported on this device.");
      }
    }
  }, [isIOS, pattern]); // Include pattern in dependencies

  return { triggerHaptic };
};

// Specific haptic hooks

/** Hook for a standard single haptic feedback (150ms) */
export const useSingleHaptic = () => {
  return useHaptic(100);
};

/** Hook for a short, soft haptic feedback (50ms) */
export const useSoftHaptic = () => {
  return useHaptic(30);
};

/** Hook for a double soft haptic feedback (50ms vibrate, 50ms pause, 50ms vibrate) */
export const useDoubleSoftHaptic = () => {
  // Note: The pause duration might need adjustment for desired feel.
  // iOS will attempt two standard clicks with a 60ms delay.
  return useHaptic([30, 30, 30]);
};

// For backwards compatibility (though the signature changed)
export default useHaptic;
